import React, { useState } from 'react';
import { Document, Packer, Paragraph, Table, TableRow, TableCell, TextRun, BorderStyle, WidthType, AlignmentType } from 'docx';
import { saveAs } from 'file-saver';
import PropTypes from 'prop-types';
import SenetPrintDialog from './SenetPrintDialog';

const SenetGenerator = ({ paymentData, paymentDetails, user }) => {
    const [dialogVisible, setDialogVisible] = useState(false);

    const customerName = `${paymentData.name || ''} ${paymentData.surname || ''}`;
    const guarantorName = `${paymentData?.guarantorName || ''} ${paymentData?.guarantorSurname || ''}`;

    const customerAddress = `${paymentData?.customerDetailOfAddress || ''} - ${paymentData?.customerDistrictOfAddress || ''} / ${paymentData?.customerCountryOfAddress || ''}`;

    const {
        guarantorDetailOfAddress,
        guarantorDistrictOfAddress,
        guarantorCountryOfAddress
    } = paymentData || {};

    const hasAnyAddressInfo = guarantorDetailOfAddress || guarantorDistrictOfAddress || guarantorCountryOfAddress;

    const guarantorAddress = hasAnyAddressInfo
        ? `${guarantorDetailOfAddress || ''} - ${guarantorDistrictOfAddress || ''} / ${guarantorCountryOfAddress || ''}`
        : ' ';

    function numberToTurkishWords(num) {
        const ones = ['', 'bir', 'iki', 'üç', 'dört', 'beş', 'altı', 'yedi', 'sekiz', 'dokuz'];
        const tens = ['', 'on', 'yirmi', 'otuz', 'kırk', 'elli', 'altmış', 'yetmiş', 'seksen', 'doksan'];
        const scales = ['', 'bin', 'milyon', 'milyar', 'trilyon'];

        if (num === 0) return 'sıfır';

        function convertGroup(n) {
            let result = '';

            if (n >= 100) {
                result += (n < 200 ? 'yüz' : ones[Math.floor(n / 100)] + ' yüz');
                n %= 100;
                if (n > 0) result += ' ';
            }

            if (n >= 10) {
                result += tens[Math.floor(n / 10)];
                n %= 10;
                if (n > 0) result += ' ';
            }

            if (n > 0) {
                result += ones[n];
            }

            return result;
        }

        let result = '';
        let groupCount = 0;

        while (num > 0) {
            const n = num % 1000;

            if (n !== 0) {
                let groupStr = '';

                if (n === 1 && groupCount === 1) {
                    groupStr = scales[groupCount];
                } else {
                    groupStr = convertGroup(n) + (groupCount > 0 ? ' ' + scales[groupCount] : '');
                }

                result = groupStr + (result ? ' ' + result : '');
            }

            num = Math.floor(num / 1000);
            groupCount++;
        }

        return result;
    }

    function formatAmountToTurkishWords(formattedAmount) {
        const cleanedAmount = formattedAmount.replace(/[^\d.,]/g, '');
        const parts = cleanedAmount.replace(/\./g, '').split(',');

        const lira = parseInt(parts[0], 10);
        const kurus = parts.length > 1 ? parseInt(parts[1].padEnd(2, '0').substring(0, 2), 10) : 0;

        let result = '';

        if (lira > 0) {
            const liraText = numberToTurkishWords(lira);
            result += liraText.charAt(0).toUpperCase() + liraText.slice(1);
            result += ' Türk Lirası';
        }

        if (kurus > 0) {
            if (lira > 0) result += ' ';

            const kurusText = numberToTurkishWords(kurus);
            result += kurusText.charAt(0).toUpperCase() + kurusText.slice(1);
            result += ' Kuruş';
        }

        return result || 'Sıfır Türk Lirası';
    }

    // filterPaymentDetails fonksiyonunu güncelleyin
    const filterPaymentDetails = (details, options) => {
        const { printType, selectedPartialInstallments } = options;

        // Önce status.id === 3 olanları filtrele
        let filteredDetails = details.filter(item => item.status && item.status.id === 3);

        if (printType === 'all') {
            // Zaten filtrelenmiş, ek filtreleme gerekmez
        } else if (printType === 'normal') {
            filteredDetails = filteredDetails.filter(item => !item.isPartialPayment || item.isPartialPayment.id === 0);
        } else if (printType === 'partial') {
            filteredDetails = filteredDetails.filter(item => item.isPartialPayment && item.isPartialPayment.id === 1);

            // If specific partial installments are selected, filter by installmentOrder
            if (selectedPartialInstallments && selectedPartialInstallments.length > 0) {
                const selectedOrders = selectedPartialInstallments.map(item => item.installmentOrder);
                filteredDetails = filteredDetails.filter(item =>
                    selectedOrders.includes(item.installmentOrder)
                );
            }
        }

        return filteredDetails;
    };

    const generateWordDocument = async (options) => {
        try {
            const filteredDetails = filterPaymentDetails(paymentDetails, options);
            if (filteredDetails.length === 0) {
                alert('Seçilen kriterlere uygun senet bulunamadı!');
                return;
            }

            const doc = createSenetWord(paymentData, filteredDetails, options);
            const blob = await Packer.toBlob(doc);
            saveAs(blob, `${customerName}_${new Date().toLocaleDateString('tr-TR')}_Senet.docx`);
        } catch (error) {
            console.error('Error generating Word document:', error);
        }
    };

    const createSenetWord = (paymentData, paymentDetails, options) => {
        const sortedDetails = [...paymentDetails].sort((a, b) => {
            // First sort by installmentOrder
            if (a.installmentOrder !== b.installmentOrder) {
                return a.installmentOrder - b.installmentOrder;
            }

            // Then by partialPaymentInstallmentOrder for partial payments
            const aPartialOrder = a.partialPaymentInstallmentOrder || 0;
            const bPartialOrder = b.partialPaymentInstallmentOrder || 0;
            return aPartialOrder - bPartialOrder;
        });

        // Sıralı senet listesine ardışık numaralar ekleyelim
        const numberedDetails = sortedDetails.map((detail, index) => ({
            ...detail,
            sequentialNumber: index + 1 // 1'den başlayan ardışık numara
        }));

        const totalSenets = numberedDetails.length;
        const pagesNeeded = Math.ceil(totalSenets / 3);

        const sections = [];

        for (let pageIndex = 0; pageIndex < pagesNeeded; pageIndex++) {
            const startIdx = pageIndex * 3;
            const endIdx = Math.min(startIdx + 3, totalSenets);
            const senetsOnPage = numberedDetails.slice(startIdx, endIdx);

            sections.push(generatePageSection(paymentData, senetsOnPage, options));
        }

        const doc = new Document({
            sections: sections
        });

        return doc;
    };

    const generatePageSection = (paymentData, senetsOnPage, options) => {
        const children = [];
        // A4 boyutları ve hesaplamalar
        const a4Height = 16838; // A4 yüksekliği (twips)
        const marginTop = 720;   // Üst margin (twips)
        const marginBottom = 720; // Alt margin (twips)
        const usableHeight = a4Height - marginTop - marginBottom;
        const senetHeight = Math.floor(usableHeight / 3); // Her senet için eşit yükseklik

        senetsOnPage.forEach((detail, index) => {
            // Senet içeriğini ekle
            children.push(...generateSenetContent(paymentData, detail, options));

            // Son senet değilse kesim çizgisi ekle
            if (index < senetsOnPage.length - 1) {
                children.push(
                    new Paragraph({
                        children: [
                            new TextRun({
                                text: "✂----------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------✂",
                                size: 16,
                                color: "666666"
                            })
                        ],
                        alignment: AlignmentType.CENTER,
                        spacing: {
                            before: 200,
                            after: 200
                        },
                        border: {
                            top: {
                                style: BorderStyle.DASHED,
                                size: 2,
                                color: "CCCCCC"
                            }
                        }
                    })
                );
            }

            // Boş alan doldurucu paragraflar ekle (sayfa bölümlerini eşit yapmak için)
            if (index < 2) { // İlk iki senet için
                for (let i = 0; i < 3; i++) {
                    children.push(new Paragraph({ text: "" }));
                }
            }
        });

        // Eğer sayfada 3'ten az senet varsa, boş bölümler ekle
        while (senetsOnPage.length < 3) {
            children.push(
                new Paragraph({
                    children: [
                        new TextRun({
                            text: "✂----------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------✂",
                            size: 16,
                            color: "666666"
                        })
                    ],
                    alignment: AlignmentType.CENTER,
                    spacing: {
                        before: 200,
                        after: 200
                    },
                    border: {
                        top: {
                            style: BorderStyle.DASHED,
                            size: 2,
                            color: "CCCCCC"
                        }
                    }
                })
            );

            // Boş senet bölümü
            children.push(
                new Paragraph({
                    text: "[BOŞ SENET BÖLÜMÜ]",
                    alignment: AlignmentType.CENTER,
                    spacing: { before: 1000, after: 1000 }
                })
            );

            senetsOnPage.push({}); // Döngüyü sonlandırmak için dummy ekle
        }

        return {
            properties: {
                page: {
                    size: {
                        width: 11906,  // A4 genişlik (210mm)
                        height: 16838  // A4 yükseklik (297mm)
                    },
                    margin: {
                        top: marginTop,
                        right: 720,
                        bottom: marginBottom,
                        left: 720
                    }
                },
                borders: {
                    pageBorderTop: {
                        style: BorderStyle.SINGLE,
                        size: 1,
                        color: "000000"
                    },
                    pageBorderRight: {
                        style: BorderStyle.SINGLE,
                        size: 1,
                        color: "000000"
                    },
                    pageBorderBottom: {
                        style: BorderStyle.SINGLE,
                        size: 1,
                        color: "000000"
                    },
                    pageBorderLeft: {
                        style: BorderStyle.SINGLE,
                        size: 1,
                        color: "000000"
                    }
                }
            },
            children: children
        };
    };

    const generateSenetContent = (paymentData, detail, options) => {
        const { includeGuarantor } = options;

        const dueDate = formatDate(detail.installmentDate);
        const dueDateWithString = formatDateWithString(detail.installmentDate);
        const dateOfRecorded = new Date(paymentData?.dateOfRecorded).toLocaleDateString('tr-TR');
        const totalAmount = detail.totalAmountOfInstallments;
        const formattedAmount = formatCurrency(totalAmount);

        const installmentLabel = detail.sequentialNumber.toString();

        const headerTable = new Table({
            width: {
                size: 100,
                type: WidthType.PERCENTAGE
            },
            borders: {
                top: { style: BorderStyle.SINGLE, size: 1 },
                bottom: { style: BorderStyle.SINGLE, size: 1 },
                left: { style: BorderStyle.SINGLE, size: 1 },
                right: { style: BorderStyle.SINGLE, size: 1 },
                insideHorizontal: { style: BorderStyle.SINGLE, size: 1 },
                insideVertical: { style: BorderStyle.SINGLE, size: 1 }
            },
            rows: [
                new TableRow({
                    children: [
                        new TableCell({
                            width: { size: 20, type: WidthType.PERCENTAGE },
                            children: [new Paragraph({
                                text: "Vade",
                                alignment: AlignmentType.CENTER
                            })],
                        }),
                        new TableCell({
                            width: { size: 20, type: WidthType.PERCENTAGE },
                            children: [new Paragraph({
                                text: "Ödeme Tarihi",
                                alignment: AlignmentType.CENTER
                            })],
                        }),
                        new TableCell({
                            width: { size: 30, type: WidthType.PERCENTAGE },
                            children: [new Paragraph({
                                text: "₺",
                                alignment: AlignmentType.CENTER
                            })],
                        }),
                        new TableCell({
                            width: { size: 20, type: WidthType.PERCENTAGE },
                            children: [new Paragraph({
                                text: "Kuruş",
                                alignment: AlignmentType.CENTER
                            })],
                        }),
                        new TableCell({
                            width: { size: 10, type: WidthType.PERCENTAGE },
                            children: [new Paragraph({
                                text: "No.",
                                alignment: AlignmentType.CENTER
                            })],
                        }),
                    ]
                }),
                new TableRow({
                    children: [
                        new TableCell({
                            children: [new Paragraph({
                                text: dueDate,
                                alignment: AlignmentType.CENTER
                            })],
                        }),
                        new TableCell({
                            children: [new Paragraph({
                                text: dueDate,
                                alignment: AlignmentType.CENTER
                            })],
                        }),
                        new TableCell({
                            children: [new Paragraph({
                                text: `#${Math.floor(totalAmount).toLocaleString('tr-TR')}#`,
                                alignment: AlignmentType.CENTER
                            })],
                        }),
                        new TableCell({
                            children: [new Paragraph({
                                text: `#${getDecimalPart(totalAmount)}#`,
                                alignment: AlignmentType.CENTER
                            })],
                        }),
                        new TableCell({
                            children: [new Paragraph({
                                text: installmentLabel,
                                alignment: AlignmentType.CENTER
                            })],
                        }),
                    ]
                }),
            ]
        });

        const mainTextParagraphs = [
            new Paragraph({ text: "" }),
            new Paragraph({
                children: [
                    new TextRun(`   İş bu emre yazılı senedimin mukabilinde ${dueDateWithString} tarihinde Sayın, ${user.name} ${user.surname} veyahut emrühavale ........................................ Yukarıda yazılı yalnız #${formatAmountToTurkishWords(formattedAmount)}# ödeyeceğim. Bedeli nakden alınmıştır. İşbu bono vadesinde ödenmediği taktirde diğer bonoların da muacceliyet kesbedeceğini, Avukat ücreti dahil bütün mahkeme masraflarını ödeyeceğimi, ihtilaf Vukuunda ${user.districtOfWorkAddress} / ${user.countryOfWorkAddress}  Mahkemelerinin selahiyetini, şimdiden kabul eylerim.`)
                ],
                spacing: { after: 100 }
            })
        ];

        const customerInfoCells = [
            new Paragraph({
                children: [
                    new TextRun("İsim/Unvan   : "),
                    new TextRun({ text: customerName })
                ]
            }),
            new Paragraph({
                children: [
                    new TextRun(`Vergi No/T.C. Kimlik No :`),
                    new TextRun({ text: paymentData.customerIdendityNumber || '' })
                ],
            }),
            new Paragraph({
                children: [
                    new TextRun("Adres      : "),
                    new TextRun(`${customerAddress}`)
                ],
            })
        ];

        if (includeGuarantor) {
            customerInfoCells.push(
                new Paragraph({ text: "" }),
                new Paragraph({
                    children: [
                        new TextRun("Kefil       : "),
                        new TextRun({ text: guarantorName })
                    ]
                }),
                new Paragraph({
                    children: [
                        new TextRun("Vergi No/T.C. Kimlik No :"),
                        new TextRun({ text: paymentData.guarantorIdendityNumber || '' })
                    ]
                }),
                new Paragraph({
                    children: [
                        new TextRun("Adres      : "),
                        new TextRun(`${guarantorAddress}`)
                    ]
                })
            );
        }

        const bottomTable = new Table({
            width: { size: 100, type: WidthType.PERCENTAGE },
            borders: {
                top: { style: BorderStyle.NONE },
                bottom: { style: BorderStyle.NONE },
                left: { style: BorderStyle.NONE },
                right: { style: BorderStyle.NONE },
                insideHorizontal: { style: BorderStyle.NONE },
                insideVertical: { style: BorderStyle.NONE }
            },
            rows: [
                new TableRow({
                    children: [
                        new TableCell({
                            width: { size: 65, type: WidthType.PERCENTAGE },
                            borders: {
                                top: { style: BorderStyle.NONE },
                                bottom: { style: BorderStyle.NONE },
                                left: { style: BorderStyle.NONE },
                                right: { style: BorderStyle.NONE }
                            },
                            children: customerInfoCells
                        }),
                        new TableCell({
                            width: { size: 35, type: WidthType.PERCENTAGE },
                            borders: {
                                top: { style: BorderStyle.NONE },
                                bottom: { style: BorderStyle.NONE },
                                left: { style: BorderStyle.NONE },
                                right: { style: BorderStyle.NONE }
                            },
                            children: [
                                new Paragraph({
                                    children: [
                                        new TextRun(`Düzenleme Tarihi : ${dateOfRecorded}`)
                                    ],
                                    alignment: AlignmentType.RIGHT
                                }),
                                new Paragraph({
                                    children: [
                                        new TextRun(`Düzenleme Yeri : ${user.districtOfWorkAddress} / ${user.countryOfWorkAddress}`)
                                    ],
                                    alignment: AlignmentType.RIGHT,
                                }),
                                new Paragraph({ text: "" }),

                                new Paragraph({
                                    children: [
                                        new TextRun("      İmza                         İmza")
                                    ],
                                    alignment: AlignmentType.CENTER
                                }),
                            ]
                        })
                    ]
                })
            ]
        });

        return [
            headerTable,
            ...mainTextParagraphs,
            bottomTable,
            // Senet sonuna boşluk ekle
            new Paragraph({
                text: "",
                spacing: { after: 400 }
            })
        ];
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return new Date().toLocaleDateString('tr-TR');
        const date = new Date(dateStr);
        return date.toLocaleDateString('tr-TR');
    };

    const formatDateWithString = (dateStr) => {
        if (!dateStr) return new Date().toLocaleDateString('tr-TR', {
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        });

        const date = new Date(dateStr);
        return date.toLocaleDateString('tr-TR', {
            day: '2-digit',
            month: 'long',
            year: 'numeric'
        });
    };

    const formatCurrency = (amount) => {
        return amount.toLocaleString('tr-TR') + ' TL';
    };

    const getDecimalPart = (amount) => {
        return Math.round((amount - Math.floor(amount)) * 100);
    };

    const handleDialogConfirm = (options) => {
        setDialogVisible(false);
        generateWordDocument(options);
    };

    return (
        <>
            <button
                onClick={() => setDialogVisible(true)}
                className="
                    rounded-xl
                    shadow-md
                    cursor-pointer
                    text-yellow-700
                    dark:text-white
                    border
                    border-yellow-700
                    hover:border-yellow-500
                    dark:border-yellow-600
                    dark:hover:border-yellow-700
                    bg-transparent
                    hover:bg-yellow-500
                    hover:text-white
                    dark:bg-yellow-600
                    dark:hover:bg-yellow-700
                    dark:shadow-lg
                    px-3
                    transition-all
                    duration-200
                "
            >
                <i className="pi pi-file-word mr-2"></i>
                Senet Yazdır (Word)
            </button>

            <SenetPrintDialog
                visible={dialogVisible}
                onHide={() => setDialogVisible(false)}
                paymentDetails={paymentDetails}
                onConfirm={handleDialogConfirm}
            />
        </>
    );
};

SenetGenerator.propTypes = {
    paymentData: PropTypes.object.isRequired,
    paymentDetails: PropTypes.array.isRequired
};

export default SenetGenerator;